import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { TimezoneInput } from './design-system/TimezoneInput';
import { TimePickerField } from './TimePickerField';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { DataListSyncRecurrence, DataListSyncRecurrenceType } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export interface SchedulePickerProps {
  /** undefined = no schedule configured (off) — matches the pre-existing
   * "blank = sync-on-read only" contract this replaces. */
  value: DataListSyncRecurrence | undefined;
  onChange: (next: DataListSyncRecurrence | undefined) => void;
  disabled?: boolean;
}

const RECURRENCE_TYPES: DataListSyncRecurrenceType[] = ['minutes', 'hours', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const QUARTER_MONTH_LABELS = ['Jan', 'Apr', 'Jul', 'Oct'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (n: number) => String(n).padStart(2, '0');

const MINUTE_PRESETS = [5, 10, 15, 30, 45];
const HOUR_PRESETS = [1, 2, 3, 6, 12];
const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => i + 1);

/** The user's own IANA timezone — schedules are entered in local time and
 * the server runs the cron in this zone (it stores/compares in UTC). */
export const getDeviceTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * Reusable recurrence picker — every N minutes/hours, daily/weekly/monthly/
 * quarterly/yearly at a fixed time. Emits a structured DataListSyncRecurrence,
 * not a cron string — cron generation lives server-side
 * (data-lists/utils/recurrence-to-cron.util.ts) so no client needs to
 * understand cron syntax. Not data-list-specific; any future scheduling UI
 * can reuse this the same way HttpRequestBuilder is reused for any raw HTTP
 * config, not just webhook data lists.
 */
export const SchedulePicker: React.FC<SchedulePickerProps> = ({ value, onChange, disabled = false }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();

  const enabled = !!value;
  const type = value?.type || 'minutes';

  const patch = (p: Partial<DataListSyncRecurrence>) => {
    if (!value) return;
    onChange({ ...value, ...p });
  };

  const setType = (nextType: DataListSyncRecurrenceType) => {
    // Reset type-specific fields to sane defaults on switch, same
    // "don't leak stale fields across mode switches" convention as
    // FieldMatcher's setMode. The timezone carries over (defaulting to the
    // device's own zone) so times are always entered in local time.
    const timezone = value?.timezone || getDeviceTimezone();
    if (nextType === 'minutes' || nextType === 'hours') {
      onChange({ type: nextType, interval: nextType === 'minutes' ? 15 : 1, timezone });
    } else if (nextType === 'daily') {
      onChange({ type: nextType, hour: 9, minute: 0, timezone });
    } else if (nextType === 'weekly') {
      onChange({ type: nextType, hour: 9, minute: 0, daysOfWeek: [1], timezone });
    } else if (nextType === 'monthly' || nextType === 'quarterly') {
      onChange({ type: nextType, hour: 9, minute: 0, dayOfMonth: 1, timezone });
    } else {
      onChange({ type: nextType, hour: 9, minute: 0, dayOfMonth: 1, month: 1, timezone });
    }
  };

  const toggleWeekday = (day: number) => {
    const current = value?.daysOfWeek?.length ? value.daysOfWeek : [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort();
    patch({ daysOfWeek: next.length > 0 ? next : [day] });
  };

  const summary = enabled ? describeRecurrence(value!, t) : '';

  return (
    <View>
      <TouchableOpacity
        style={[styles.enableRow, { flexDirection: rtl.rowDirection }]}
        onPress={() => !disabled && onChange(enabled ? undefined : { type: 'daily', hour: 9, minute: 0, timezone: getDeviceTimezone() })}
        disabled={disabled}
      >
        <View style={[styles.checkbox, enabled && styles.checkboxActive]} />
        <Text style={[styles.enableLabel, { textAlign: rtl.textStart }]}>{t('schedulePicker.enable')}</Text>
      </TouchableOpacity>

      {enabled && (
        <>
          <View style={[styles.typeRow, { flexDirection: rtl.rowDirection }]}>
            {RECURRENCE_TYPES.map((rt) => (
              <TouchableOpacity
                key={rt}
                style={[styles.typePill, type === rt && styles.typePillActive]}
                onPress={() => !disabled && setType(rt)}
                disabled={disabled}
              >
                <Text style={[styles.typePillText, type === rt && styles.typePillTextActive]}>{t(`schedulePicker.type.${rt}` as any)}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {(type === 'minutes' || type === 'hours') && (
            <View style={styles.block}>
              <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>
                {t(type === 'minutes' ? 'schedulePicker.everyMinutesLabel' : 'schedulePicker.everyHoursLabel')}
              </Text>
              <View style={[styles.typeRow, { flexDirection: rtl.rowDirection }]}>
                {(type === 'minutes' ? MINUTE_PRESETS : HOUR_PRESETS).map((n) => {
                  const active = (value?.interval ?? 1) === n;
                  return (
                    <TouchableOpacity key={n} style={[styles.weekdayPill, active && styles.typePillActive]} onPress={() => !disabled && patch({ interval: n })} disabled={disabled}>
                      <Text style={[styles.typePillText, active && styles.typePillTextActive]}>{n}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={[styles.stepperRow, { flexDirection: rtl.rowDirection }]}>
                <TouchableOpacity style={styles.stepperButton} onPress={() => !disabled && patch({ interval: clamp((value?.interval ?? 1) - 1, 1, type === 'minutes' ? 59 : 23) })} disabled={disabled}>
                  <Text style={styles.stepperButtonText}>−</Text>
                </TouchableOpacity>
                <Text style={styles.stepperValue}>{value?.interval ?? 1}</Text>
                <TouchableOpacity style={styles.stepperButton} onPress={() => !disabled && patch({ interval: clamp((value?.interval ?? 1) + 1, 1, type === 'minutes' ? 59 : 23) })} disabled={disabled}>
                  <Text style={styles.stepperButtonText}>+</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {(type === 'daily' || type === 'weekly' || type === 'monthly' || type === 'quarterly' || type === 'yearly') && (
            <View style={styles.timeRow} pointerEvents={disabled ? 'none' : 'auto'}>
              <TimePickerField
                label={t('schedulePicker.timeLabel')}
                value={`${pad2(value?.hour ?? 0)}:${pad2(value?.minute ?? 0)}`}
                onChange={(time) => {
                  const [h, m] = time.split(':').map((n) => parseInt(n, 10));
                  patch({ hour: clamp(h || 0, 0, 23), minute: clamp(m || 0, 0, 59) });
                }}
              />
            </View>
          )}

          {type === 'weekly' && (
            <View style={[styles.weekdayRow, { flexDirection: rtl.rowDirection }]}>
              {WEEKDAY_LABELS.map((label, day) => {
                const active = (value?.daysOfWeek || []).includes(day);
                return (
                  <TouchableOpacity
                    key={day}
                    style={[styles.weekdayPill, active && styles.typePillActive]}
                    onPress={() => !disabled && toggleWeekday(day)}
                    disabled={disabled}
                  >
                    <Text style={[styles.typePillText, active && styles.typePillTextActive]}>{label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {(type === 'monthly' || type === 'quarterly' || type === 'yearly') && (
            <View style={styles.block}>
              <Text style={[styles.fieldLabel, { textAlign: rtl.textStart }]}>{t('schedulePicker.dayOfMonthLabel')}</Text>
              <View style={[styles.typeRow, { flexDirection: rtl.rowDirection }]}>
                {DAYS_OF_MONTH.map((d) => {
                  const active = (value?.dayOfMonth ?? 1) === d;
                  return (
                    <TouchableOpacity key={d} style={[styles.dayCell, active && styles.typePillActive]} onPress={() => !disabled && patch({ dayOfMonth: d })} disabled={disabled}>
                      <Text style={[styles.typePillText, active && styles.typePillTextActive]}>{d}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}
          {type === 'quarterly' && (
            <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('schedulePicker.quarterlyHint', { months: QUARTER_MONTH_LABELS.join('/') })}</Text>
          )}

          {type === 'yearly' && (
            <View style={[styles.typeRow, { flexDirection: rtl.rowDirection, marginTop: spacing.xs }]}>
              {MONTH_LABELS.map((label, i) => {
                const m = i + 1;
                const active = (value?.month ?? 1) === m;
                return (
                  <TouchableOpacity key={m} style={[styles.weekdayPill, active && styles.typePillActive]} onPress={() => !disabled && patch({ month: m })} disabled={disabled}>
                    <Text style={[styles.typePillText, active && styles.typePillTextActive]}>{label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {type !== 'minutes' && type !== 'hours' && (
            <View style={styles.block} pointerEvents={disabled ? 'none' : 'auto'}>
              <TimezoneInput
                label={t('schedulePicker.timezoneLabel')}
                value={value?.timezone || 'UTC'}
                onChange={(tz) => patch({ timezone: tz })}
              />
            </View>
          )}

          <Text style={[styles.summary, { textAlign: rtl.textStart }]}>{summary}</Text>
        </>
      )}
    </View>
  );
};

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Thin client-side mirror of recurrenceToCron's logic, for display only —
 * intentionally simple since it's never sent anywhere, just read by the user. */
function describeRecurrence(r: DataListSyncRecurrence, t: (key: any, params?: Record<string, any>) => string): string {
  const time = `${pad2(r.hour ?? 0)}:${pad2(r.minute ?? 0)}`;
  const tz = r.timezone || 'UTC';
  switch (r.type) {
    case 'minutes':
      return t('schedulePicker.summary.minutes', { n: r.interval || 1 });
    case 'hours':
      return t('schedulePicker.summary.hours', { n: r.interval || 1 });
    case 'daily':
      return t('schedulePicker.summary.daily', { time, tz });
    case 'weekly': {
      const days = (r.daysOfWeek?.length ? r.daysOfWeek : [0]).map((d) => WEEKDAY_LABELS[d]).join(', ');
      return t('schedulePicker.summary.weekly', { days, time, tz });
    }
    case 'monthly':
      return t('schedulePicker.summary.monthly', { day: r.dayOfMonth ?? 1, time, tz });
    case 'quarterly':
      return t('schedulePicker.summary.quarterly', { day: r.dayOfMonth ?? 1, months: QUARTER_MONTH_LABELS.join('/'), time, tz });
    case 'yearly':
      return t('schedulePicker.summary.yearly', { day: r.dayOfMonth ?? 1, month: MONTH_LABELS[(r.month ?? 1) - 1], time, tz });
    default:
      return '';
  }
}

const getStyles = () => StyleSheet.create({
  enableRow: { alignItems: 'center', gap: spacing.xs, marginBottom: spacing.sm },
  checkbox: {
    width: 20, height: 20, borderRadius: 4,
    borderWidth: 2, borderColor: colors.border,
    backgroundColor: 'transparent',
  },
  checkboxActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  enableLabel: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  typeRow: { flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  typePill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.full ?? 999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  typePillActive: { backgroundColor: withAlpha(colors.primary, 0.08), borderColor: colors.primary },
  typePillText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, fontFamily: typography.fontFamily.medium },
  typePillTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  timeRow: { marginBottom: spacing.sm },
  block: { marginBottom: spacing.sm },
  fieldLabel: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.medium, marginBottom: spacing.xs },
  stepperRow: { alignItems: 'center', gap: spacing.sm },
  stepperButton: {
    width: 36, height: 36, borderRadius: 18,
    borderWidth: 1, borderColor: colors.border,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: colors.background.surface,
  },
  stepperButtonText: { fontSize: typography.fontSize.lg, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  stepperValue: { minWidth: 32, textAlign: 'center', fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  dayCell: {
    width: 40, paddingVertical: spacing.xs, alignItems: 'center',
    borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  weekdayRow: { flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs, marginBottom: spacing.sm },
  weekdayPill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginTop: -spacing.xs / 2, marginBottom: spacing.sm },
  summary: { fontSize: typography.fontSize.sm, color: colors.text.secondary, fontFamily: typography.fontFamily.medium, marginTop: spacing.xs },
});
